import { useEffect, useRef, useState } from 'react';

/**
 * Photographs, large. Spec 008 FR-801 to FR-804.
 *
 * Guy, 5 October 2026: he could not see a product photo bigger than a thumbnail
 * anywhere, and a product with several photos showed only one. Staff pack from
 * these screens, so a picture that a person can recognise saves a walk to the
 * wrong tub.
 *
 * One viewer for every screen, so a person learns it once: a click opens it,
 * the arrow keys and the buttons move between photos, Escape closes it, and
 * focus goes back to what opened it.
 */

export interface Photo {
  url: string;
  alt: string;
}

/**
 * The photos of one stock line, in the order a person wants them: the
 * colourway shot first, because a line is a colour and a size, then the
 * product's own photos by their sort order. The same file is never shown twice.
 */
export function photosOf(
  variant: {
    colour?: string | null;
    image_url?: string | null;
    products?: { product_images?: Array<{ url: string; alt: string; sort: number }> | null } | null;
  } | null | undefined,
  name: string,
): Photo[] {
  if (!variant) return [];
  const out: Photo[] = [];
  if (variant.image_url) {
    out.push({ url: variant.image_url, alt: [name, variant.colour].filter(Boolean).join(', ') });
  }
  const rest = [...(variant.products?.product_images ?? [])].sort((a, b) => a.sort - b.sort);
  for (const p of rest) {
    if (!out.some((o) => o.url === p.url)) out.push({ url: p.url, alt: p.alt || name });
  }
  return out;
}

export function PhotoViewer({
  photos, start = 0, title, onClose,
}: {
  photos: Photo[];
  start?: number;
  title?: string;
  onClose: () => void;
}) {
  const [i, setI] = useState(Math.min(Math.max(start, 0), photos.length - 1));
  const closeRef = useRef<HTMLButtonElement>(null);
  const opener = useRef<Element | null>(null);
  const many = photos.length > 1;
  const go = (d: number) => setI((n) => (n + d + photos.length) % photos.length);

  useEffect(() => {
    opener.current = document.activeElement;
    closeRef.current?.focus();
    const key = (e: KeyboardEvent) => {
      // Capture phase and stopped here: the viewer opens over the stock side
      // panel, which also closes on Escape. One press closes one thing.
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); onClose(); }
      if (e.key === 'ArrowRight' && many) { e.preventDefault(); go(1); }
      if (e.key === 'ArrowLeft' && many) { e.preventDefault(); go(-1); }
    };
    document.addEventListener('keydown', key, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', key, true);
      document.body.style.overflow = overflow;
      (opener.current as HTMLElement | null)?.focus?.();
    };
    // Set once. `go` reads the photo count, which does not change while open.
  }, []);

  // A swipe on a phone moves between photos, as the arrow keys do.
  const touchX = useRef<number | null>(null);

  const photo = photos[i];
  if (!photo) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title ? `Photos of ${title}` : 'Photos'}
      className="fixed inset-0 z-[100] flex flex-col bg-[rgba(15,17,21,0.92)] text-white"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        const from = touchX.current;
        const to = e.changedTouches[0]?.clientX;
        touchX.current = null;
        if (from == null || to == null || !many) return;
        if (Math.abs(to - from) > 50) go(to < from ? 1 : -1);
      }}
    >
      <div className="flex items-center justify-between gap-4 px-4 py-3 text-[14px]">
        <span className="min-w-0 truncate">
          {title}
          {many && <span className="ml-2 text-white/70">{i + 1} of {photos.length}</span>}
        </span>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="h-11 rounded-[8px] border border-white/30 px-4 font-semibold hover:bg-white/10"
        >
          Close
        </button>
      </div>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {many && (
          <button
            type="button"
            aria-label="Previous photo"
            onClick={() => go(-1)}
            className="absolute left-3 top-1/2 h-12 w-12 -translate-y-1/2 rounded-full bg-white/15 text-[24px] hover:bg-white/25"
          >
            ‹
          </button>
        )}
        <img
          key={photo.url}
          src={photo.url}
          alt={photo.alt}
          className="max-h-full max-w-full rounded-[6px] object-contain"
        />
        {many && (
          <button
            type="button"
            aria-label="Next photo"
            onClick={() => go(1)}
            className="absolute right-3 top-1/2 h-12 w-12 -translate-y-1/2 rounded-full bg-white/15 text-[24px] hover:bg-white/25"
          >
            ›
          </button>
        )}
      </div>

      {many && (
        <div className="flex justify-center gap-2 overflow-x-auto px-4 py-3">
          {photos.map((p, n) => (
            <button
              key={p.url}
              type="button"
              aria-label={`Photo ${n + 1}`}
              aria-current={n === i}
              onClick={() => setI(n)}
              className={
                'h-14 w-14 shrink-0 overflow-hidden rounded-[6px] border-2 '
                + (n === i ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100')
              }
            >
              <img src={p.url} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A large photo with the others under it, for a detail panel. Spec 008 FR-807.
 *
 * A small photo CHANGES the large one; it does not open the viewer. Christian,
 * 5 October 2026: opening a full-screen viewer to look at the next photo is a
 * step too many when the panel already has room for it. The large photo opens
 * the viewer, at the photo it shows.
 */
export function PhotoGallery({ photos, title }: { photos: Photo[]; title: string }) {
  const [shown, setShown] = useState(0);
  const [open, setOpen] = useState(false);
  if (photos.length === 0) return null;
  const current = photos[Math.min(shown, photos.length - 1)]!;
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Show ${title} full screen`}
        className="group relative overflow-hidden rounded-[10px] border border-line bg-raised"
      >
        <img
          key={current.url}
          src={current.url}
          alt={current.alt}
          className="mx-auto max-h-[320px] w-full object-contain"
        />
        <span className="absolute bottom-2 right-2 rounded-[6px] bg-ink/80 px-2 py-1 text-[12px] text-panel opacity-90 group-hover:opacity-100">
          {photos.length > 1 ? `${shown + 1} of ${photos.length} · full screen` : 'Full screen'}
        </span>
      </button>
      {photos.length > 1 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label={`Photos of ${title}`}>
          {photos.map((p, n) => (
            <button
              key={p.url}
              type="button"
              onClick={() => setShown(n)}
              aria-label={`Show photo ${n + 1} of ${title}`}
              aria-pressed={n === shown}
              className={
                'h-16 w-16 overflow-hidden rounded-[6px] border-2 bg-raised '
                + (n === shown ? 'border-ink' : 'border-line hover:border-line-strong')
              }
            >
              <img src={p.url} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {open && (
        <PhotoViewer photos={photos} start={shown} title={title} onClose={() => setOpen(false)} />
      )}
    </div>
  );
}
